import {
  AdjustmentsIcon,
  CalendarIcon,
  ClipboardCheckIcon,
  OfficeBuildingIcon,
  PencilAltIcon,
  TemplateIcon,
  TicketIcon,
  UserGroupIcon,
  UsersIcon,
} from '@heroicons/react/outline';
import * as React from 'react';
import { StaticImage } from 'gatsby-plugin-image';
import { classNames } from '../../utils';

const imgCommonClassName =
  'max-w-none rounded-xl bg-gray-900 shadow-xl ring-1 ring-gray-400/10';

const contentDefault = {
  preTitle: <>Strategic seating</>,
  title: <>What is a seating chart, really?</>,
  description: [
    <>
      Think of a seating chart (also referred to as a seating plan) as your
      blueprint for where everyone is positioned in a given space—whether in a
      classroom, at a wedding, or during a conference.
    </>,
    <>
      Its main goal is to optimize the room's dynamics, maintain order, and
      speed up seating and identification.
    </>,
  ],

  img: (
    <StaticImage
      loading="lazy"
      width={912}
      className={classNames('w-[36rem] sm:w-[46rem]', imgCommonClassName)}
      src="../../images/landing/classroom-content-04-24.jpg"
      alt="A seating chart for a classroom or event with 6-person table groups and decoration elements like plants, storage, a screen, and a whiteboard."
    />
  ),

  subTitle2: <>Why bother with a seating chart?</>,
  description2: (
    <>
      <p>
        It's all about{' '}
        <strong className="font-semibold text-gray-900">efficiency</strong> and{' '}
        <strong className="font-semibold text-gray-900">ease</strong>. With a
        seating chart, pinpointing and seating guests or students—especially at
        large events—becomes a no-sweat task. It not only speeds up taking seats
        but simplifies taking attendance.
      </p>
      <ul className="mt-8 space-y-8">
        <li className="flex gap-x-3">
          <ClipboardCheckIcon
            className="mt-1 h-5 w-5 flex-none text-indigo-600"
            aria-hidden="true"
          />
          <span>
            <strong className="font-semibold text-gray-900">
              Keep things under control.
            </strong>{' '}
            A well-planned seating chart is key to minimizing distractions and
            enhancing the overall atmosphere, whether learning in a classroom or
            maintaining decorum at big events.
          </span>
        </li>
        <li className="flex gap-x-3">
          <UserGroupIcon
            className="mt-1 h-5 w-5 flex-none text-indigo-600"
            aria-hidden="true"
          />
          <span>
            <strong className="font-semibold text-gray-900">
              Boost engagement.
            </strong>{' '}
            Cleverly organized seating fosters learning from peers and makes
            mingling and socializing a piece of cake or discourages it,
            depending on your goal.
          </span>
        </li>
      </ul>
    </>
  ),

  subTitle3: <>Best practices for creating a seating plan</>,
  description3: (
    <>
      <ul className="mt-8 space-y-8">
        <li className="flex gap-x-3">
          <TemplateIcon
            className="mt-1 h-5 w-5 flex-none text-indigo-600"
            aria-hidden="true"
          />
          <span>
            <strong className="font-semibold text-gray-900">
              Consider the layout of the space.
            </strong>{' '}
            Tailor your seating layout to the venue's size and shape. Choose
            from rows, grids, or clusters of circular tables to best meet your
            event's goals and the furniture at hand.
          </span>
        </li>
        <li className="flex gap-x-3">
          <AdjustmentsIcon
            className="mt-1 h-5 w-5 flex-none text-indigo-600"
            aria-hidden="true"
          />
          <span>
            <strong className="font-semibold text-gray-900">
              Mix and match.
            </strong>{' '}
            For a dash of dynamism, shuffle attendees based on certain criteria
            (like industries at corporate gatherings or social vibes in class)
            to spice up interactions or keep potential drama at bay (think twice
            about seating rival cousins next to each other at a wedding).
          </span>
        </li>
        <li className="flex gap-x-3">
          <PencilAltIcon
            className="mt-1 h-5 w-5 flex-none text-indigo-600"
            aria-hidden="true"
          />
          <span>
            <strong className="font-semibold text-gray-900">
              Stay flexible.
            </strong>{' '}
            Be ready to tweak your plan. Changes might be needed as more guests
            confirm, dynamics evolve, or you learn more about the group.
          </span>
        </li>
      </ul>
      <p className="mt-8">
        Leveraging these tips will help you create a seating chart that not only
        meets the logistics but also enhances everyone's experience at your
        event. Let's get everyone seated just right!
      </p>
    </>
  ),
};

type Content = typeof contentDefault;

const contentClassroom: Content = {
  preTitle: <>Strategic seating</>,
  title: <>What is a classroom seating chart, anyway?</>,
  description: [
    <>
      A classroom seating chart, or a seating plan, is a blueprint for setting
      up seats and students in a certain space—most often in classrooms.
    </>,
    <>
      Its main task is to manage space well, ensuring each student has their
      spot, which helps keep things orderly and boosts learning. Plus, it makes
      figuring out who's who a breeze.
    </>,
  ],

  img: (
    <StaticImage
      width={736}
      loading="lazy"
      className={classNames('w-[36rem] sm:w-[46rem]', imgCommonClassName)}
      src="../../images/landing/classroom-content-04-24.jpg"
      alt="A seating plan with 6-person table groups, white name labels and decoration elements like plants, storage, a screen, and a whiteboard."
    />
  ),

  subTitle2: <>Why bother with a seating chart?</>,
  description2: (
    <>
      <p className="text-gray-600">
        Crafting a seating chart is all about making things work smoothly. In
        places like schools, it helps get students seated quickly and neatly,
        speeds up roll calls, and cuts down on the usual classroom chaos.
      </p>
      <ul className="mt-8 space-y-8 text-gray-600">
        <li className="flex gap-x-3">
          <ClipboardCheckIcon
            className="mt-1 h-5 w-5 flex-none text-indigo-600"
            aria-hidden="true"
          />
          <span>
            <strong className="font-semibold text-gray-900">
              Keep things orderly.
            </strong>{' '}
            A smart seating chart minimizes distractions and limits interactions
            that might disrupt learning, which is vital for teachers who want a
            calm, productive classroom.
          </span>
        </li>
        <li className="flex gap-x-3">
          <UserGroupIcon
            className="mt-1 h-5 w-5 flex-none text-indigo-600"
            aria-hidden="true"
          />
          <span>
            <strong className="font-semibold text-gray-900">
              Boost engagement.
            </strong>{' '}
            A well-designed seating plan promotes student cooperation (while
            reducing unwanted chatter). For example, you might want to seat
            students who need extra help near the front or with a partner who
            can assist them.
          </span>
        </li>
      </ul>
    </>
  ),

  subTitle3: <>Tips for nailing your seating plan</>,
  description3: (
    <>
      <ul className="mt-8 space-y-8">
        <li className="flex gap-x-3">
          <TemplateIcon
            className="mt-1 h-5 w-5 flex-none text-indigo-600"
            aria-hidden="true"
          />
          <span>
            <strong className="font-semibold text-gray-900">
              Evaluate the classroom layout.
            </strong>{' '}
            Based on the classroom's size and shape, choose a seating
            arrangement, such as rows, clusters, or a U-shape arrangement, that
            supports your teaching aims.
          </span>
        </li>
        <li className="flex gap-x-3">
          <AdjustmentsIcon
            className="mt-1 h-5 w-5 flex-none text-indigo-600"
            aria-hidden="true"
          />
          <span>
            <strong className="font-semibold text-gray-900">
              Mix things up.
            </strong>{' '}
            Keep the energy alive by switching seats now and then based on what
            the students need or what the lesson calls for. Changes in the
            seating plan keep group dynamics smooth and the classroom fresh and
            exciting.
          </span>
        </li>
        <li className="flex gap-x-3">
          <PencilAltIcon
            className="mt-1 h-5 w-5 flex-none text-indigo-600"
            aria-hidden="true"
          />
          <span>
            <strong className="font-semibold text-gray-900">
              Remain adaptable.
            </strong>{' '}
            Be ready to tweak your seating arrangement throughout the year as
            you get to know your students better and as their needs change.
          </span>
        </li>
      </ul>
      <p className="mt-8">
        Keeping the above points in mind will help you create a seating chart
        that's not just about filling seats but also about enhancing the overall
        educational experience. Let's set up your students in the best way
        possible!
      </p>
      <p className="mt-2">
        <a
          className="font-medium text-indigo-600 hover:text-indigo-500"
          href="/articles/seating-charts-classroom-management/"
          target="_blank"
        >
          Read more about classroom management with seating charts{' '}
          <span aria-hidden="true">&rarr;</span>
        </a>
      </p>
    </>
  ),
};

const contentWedding: Content = {
  preTitle: <>Strategic seating</>,
  title: <>What exactly is a wedding seating chart, anyway?</>,
  description: [
    <>
      Think of a wedding seating chart as your master plan for arranging the
      venue layout and placing guest seats. It's not just a fancy diagram—it's
      your blueprint for boosting mingling, avoiding the drama, and keeping your
      celebration on track.
    </>,
  ],

  img: (
    <StaticImage
      loading="lazy"
      className={classNames('w-[36rem] sm:w-[46rem]', imgCommonClassName)}
      src="../../images/landing/content/event-04-24.jpg"
      width={912}
      alt="A wedding or event seating chart with long tables, name labels with portraits, and decoration elements like flowers and candles."
    />
  ),

  subTitle2: <>Why bother with a wedding seating chart?</>,
  description2: (
    <ul className="mt-8 space-y-8">
      <li className="flex gap-x-3">
        <TicketIcon
          className="mt-1 h-5 w-5 flex-none text-indigo-600"
          aria-hidden="true"
        />
        <span>
          <strong className="font-semibold text-gray-900">
            Streamline Seating.
          </strong>{' '}
          A seating chart and place cards at the tables make seating much
          quicker than having guests find their seats in a free-for-all fashion.
          Another option is to assign only a table for each guest and let them
          decide their seat as they arrive.
        </span>
      </li>
      <li className="flex gap-x-3">
        <UsersIcon
          className="mt-1 h-5 w-5 flex-none text-indigo-600"
          aria-hidden="true"
        />
        <span>
          <strong className="font-semibold text-gray-900">
            Improve the Atmosphere.
          </strong>{' '}
          Carefully placing guests helps prevent disruptions (Aunt Clara too
          close to the open bar!) and promotes a friendly environment for
          mingling.
        </span>
      </li>
      <li className="flex gap-x-3">
        <CalendarIcon
          className="mt-1 h-5 w-5 flex-none text-indigo-600"
          aria-hidden="true"
        />
        <span>
          <strong className="font-semibold text-gray-900">
            Enhance Event Flow.
          </strong>{' '}
          A well-thought-out seating plan smooths the path from "I Do" to "Let's
          Eat". It keeps everything from toasts to desserts right on schedule.
        </span>
      </li>
    </ul>
  ),

  subTitle3: <>Tips for nailing your wedding seating plan</>,
  description3: (
    <>
      <ul className="mt-8 space-y-8">
        <li className="flex gap-x-3">
          <OfficeBuildingIcon
            className="mt-1 h-5 w-5 flex-none text-indigo-600"
            aria-hidden="true"
          />
          <span>
            <strong className="font-semibold text-gray-900">
              Consider the Venue Layout.
            </strong>{' '}
            First, determine your venue's size, layout, and distinctive
            features. Is there a dance floor or panoramic windows? Next, assess
            the number and type of tables available. Finally, choose a seating
            format (e.g., small groups or family-style long tables) that
            complements the space and your wedding theme.
          </span>
        </li>
        <li className="flex gap-x-3">
          <UserGroupIcon
            className="mt-1 h-5 w-5 flex-none text-indigo-600"
            aria-hidden="true"
          />
          <span>
            <strong className="font-semibold text-gray-900">
              Know Your Guests.
            </strong>{' '}
            To make guests feel comfortable, you could group them by how you
            know them (e.g., family, friends, work colleagues). Similarly,
            consider placing guests with similar interests next to each other
            and keeping those who might not get along apart.
          </span>
        </li>
        <li className="flex gap-x-3">
          <PencilAltIcon
            className="mt-1 h-5 w-5 flex-none text-indigo-600"
            aria-hidden="true"
          />
          <span>
            <strong className="font-semibold text-gray-900">
              Stay Adaptable.
            </strong>{' '}
            Be ready to tweak the plan as RSVPs roll in and things unfold. A
            flexible seating chart allows for last-minute changes up until the
            big day, ensuring everyone has the best seat in the house.
          </span>
        </li>
      </ul>
      <p className="mt-8">
        Follow these tips, and your wedding seating chart will do more than just
        help the organizers; it'll boost the fun, enhance the mingling, and make
        your wedding day a memorable celebration. Let's seat your guests in
        style!
      </p>
    </>
  ),
};

const ContentComponent: React.FC<{ content: Content }> = ({ content }) => {
  return (
    <div className="relative isolate overflow-hidden bg-white px-6 py-24 sm:py-32 lg:overflow-visible lg:px-0">
      <div className="absolute inset-0 -z-10 overflow-hidden">
        <svg
          className="absolute left-[max(50%,25rem)] top-0 h-[64rem] w-[128rem] -translate-x-1/2 stroke-gray-200 [mask-image:radial-gradient(64rem_64rem_at_top,white,transparent)]"
          aria-hidden="true"
        >
          <defs>
            <pattern
              id="e813992c-7d03-4cc4-a2bd-151760b470a0"
              width={200}
              height={200}
              x="50%"
              y={-1}
              patternUnits="userSpaceOnUse"
            >
              <path d="M100 200V.5M.5 .5H200" fill="none" />
            </pattern>
          </defs>
          <svg x="50%" y={-1} className="overflow-visible fill-gray-50">
            <path
              d="M-100.5 0h201v201h-201Z M699.5 0h201v201h-201Z M499.5 400h201v201h-201Z M-300.5 600h201v201h-201Z"
              strokeWidth={0}
            />
          </svg>
          <rect
            width="100%"
            height="100%"
            strokeWidth={0}
            fill="url(#e813992c-7d03-4cc4-a2bd-151760b470a0)"
          />
        </svg>
      </div>
      <div className="mx-auto grid max-w-2xl grid-cols-1 gap-x-8 gap-y-16 lg:mx-0 lg:max-w-none lg:grid-cols-2 lg:items-start lg:gap-y-10">
        <div className="lg:col-span-2 lg:col-start-1 lg:row-start-1 lg:mx-auto lg:grid lg:w-full lg:max-w-7xl lg:grid-cols-2 lg:gap-x-8 lg:px-8">
          <div className="lg:pr-4">
            <div className="lg:max-w-lg">
              <p className="text-base font-semibold leading-7 text-indigo-600">
                {content.preTitle}
              </p>
              <h2 className="mt-2 mb-6 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                {content.title}
              </h2>
              {content.description.map((p, i) => (
                <p key={i} className="mt-4 text-xl leading-8 text-gray-700">
                  {p}
                </p>
              ))}
            </div>
          </div>
        </div>
        <div className="-ml-12 -mt-12 p-12 lg:sticky lg:top-4 lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:overflow-hidden">
          {content.img}
        </div>
        <div className="lg:col-span-2 lg:col-start-1 lg:row-start-2 lg:mx-auto lg:grid lg:w-full lg:max-w-7xl lg:grid-cols-2 lg:gap-x-8 lg:px-8">
          <div className="lg:pr-4">
            <div className="max-w-xl text-base leading-7 text-gray-700 lg:max-w-lg">
              <h3 className="mt-4 mb-4 text-2xl font-bold tracking-tight text-gray-900">
                {content.subTitle2}
              </h3>
              {content.description2}

              <h3 className="mt-16 text-2xl font-bold tracking-tight text-gray-900">
                {content.subTitle3}
              </h3>
              {content.description3}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const ContentDefault = () => (
  <ContentComponent content={contentDefault} />
);
export const ContentClassroom = () => (
  <ContentComponent content={contentClassroom} />
);
export const ContentWedding = () => (
  <ContentComponent content={contentWedding} />
);
